import React, { useEffect, useState } from 'react';
import styles from './MicButton.module.css';
import { useRecorder } from '../../speech/react';
import { reloadPage } from '../../utils/reloadPage';
import type { MicTranscript, STTError } from '../../speech';

export type { MicTranscript };

interface MicButtonProps {
  onStartRecording?: () => void;
  onStopRecording?: () => void;
  /** Called with the final transcript + confidence for a spoken turn. */
  onTranscript?: (result: MicTranscript) => void;
  /** Called when speech recognition fails (permission, no-speech, etc.). */
  onSttError?: (error: STTError) => void;
  /** BCP-47 language to recognize. Defaults to el-GR. */
  languageCode?: string;
  /** Phrase hints to bias backend recognition (ignored by native engines). */
  phraseHints?: string[] | null;
  /** Active run id and the step awaiting the learner, for ASR telemetry (TR-037). */
  runId?: string | null;
  stepIndex?: number | null;
  disabled?: boolean;
  /** True while the lesson is waiting for the learner to speak (BR-008, BR-014). */
  awaitingInput?: boolean;
  className?: string;
  /** Name Space as this button's key (the turn card's keyboard shortcut, #307). */
  shortcut?: boolean;
}

/**
 * Tap-to-toggle microphone button. The first tap starts a recognition turn; a
 * second tap stops it. The turn itself (provider, timers, warm-up) belongs to
 * the speech module's recorder; this component renders its state and emits
 * `{ transcript, confidence }`, never raw audio.
 */
export const MicButton: React.FC<MicButtonProps> = ({
  onStartRecording,
  onStopRecording,
  onTranscript,
  onSttError,
  languageCode = 'el-GR',
  phraseHints,
  runId,
  stepIndex,
  disabled = false,
  awaitingInput = false,
  className,
  shortcut = false,
}) => {
  const [audioLevels] = useState<number[]>(new Array(8).fill(0.3));
  // The mic delivers only silence and couldn't be recovered (iOS, #254): only
  // a reload brings it back.
  const [micUnavailable, setMicUnavailable] = useState(false);
  const recorder = useRecorder({
    onTranscript,
    onError: (error) => {
      if (error.code === 'mic-unavailable') setMicUnavailable(true);
      onSttError?.(error);
    },
  });
  const preparing = recorder.state === 'preparing';
  const isRecording = recorder.state !== 'idle' && !preparing;
  const ready = recorder.state === 'listening';
  const autoEndpoint = recorder.autoEndpoint;

  // Input no longer expected while the mic is still opening: drop the turn.
  const { cancelPreparing } = recorder;
  useEffect(() => {
    if (!awaitingInput) cancelPreparing();
  }, [awaitingInput, cancelPreparing]);

  // Tap to start or stop. The `disabled` attribute blocks taps in idle states.
  // On iOS the engine finishes a turn on its own, but a second tap still stops
  // it for immediate feedback (the button goes idle right away instead of when
  // the final result arrives).
  const handleToggle = (e: React.MouseEvent): void => {
    e.preventDefault();
    if (recorder.isActive()) {
      recorder.stop();
      onStopRecording?.();
    } else if (
      recorder.start({ languageCode, phraseHints, runId, stepIndex })
    ) {
      onStartRecording?.();
    }
  };

  const isDisabled = disabled || !awaitingInput;

  if (micUnavailable) {
    return (
      <div
        className={`${styles.micUnavailable} ${className || ''}`}
        role="alert"
        data-testid="mic-unavailable"
      >
        <p className={styles.micUnavailableText}>
          The microphone stopped working. Reload to fix it — your progress is
          saved.
        </p>
        <button type="button" className={styles.reloadBtn} onClick={reloadPage}>
          Reload
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid="mic-button"
      data-shortcut={shortcut ? 'speak' : undefined}
      aria-keyshortcuts={shortcut ? 'Space' : undefined}
      className={`${styles.voiceBtn} ${isRecording ? styles.active : ''} ${
        preparing ? styles.preparing : ''
      } ${isDisabled ? styles.disabled : ''} ${className || ''}`}
      onClick={handleToggle}
      disabled={isDisabled}
      aria-label={
        preparing
          ? 'Getting the mic ready…'
          : isRecording
            ? autoEndpoint
              ? ready
                ? 'Listening — speak now'
                : 'Getting ready…'
              : 'Recording - tap to stop'
            : !awaitingInput
              ? 'Wait for your turn to speak'
              : 'Tap to record'
      }
    >
      {preparing ? (
        <span className={styles.preparingContent} aria-hidden="true">
          <span className={styles.preparingSpinner} />
        </span>
      ) : !isRecording ? (
        <span className={styles.micIcon} aria-hidden="true">
          🎤
        </span>
      ) : (
        <div className={styles.waveformIcon} aria-hidden="true">
          {audioLevels.map((level, index) => (
            <div
              key={index}
              className={styles.waveBar}
              style={{
                height: `${level * 100}%`,
                animationDelay: `${index * 0.1}s`,
              }}
            />
          ))}
        </div>
      )}
    </button>
  );
};

export default MicButton;
